import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Save, Edit2, History, TrendingUp, Package, Euro,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  ArrowLeft, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  if (!amount) return "N/A";
  return new Intl.NumberFormat('fr-FR', { 
    style: 'currency', 
    currency: 'EUR',
    maximumFractionDigits: 0 
  }).format(amount);
};

// Catégories de produits
const CATEGORIES = {
  isolation: { label: "Isolation", color: "bg-blue-100 text-blue-700" },
  chauffage: { label: "Chauffage", color: "bg-orange-100 text-orange-700" },
  eau_chaude: { label: "Eau chaude", color: "bg-cyan-100 text-cyan-700" },
  ventilation: { label: "Ventilation", color: "bg-green-100 text-green-700" },
  menuiseries: { label: "Menuiseries", color: "bg-purple-100 text-purple-700" },
  diagnostic: { label: "Diagnostic", color: "bg-gray-100 text-gray-700" }
};

function ProductPriceEditor({ product, onSave, loading }) {
  const [editMode, setEditMode] = useState(false);
  const [prices, setPrices] = useState(product.prices);
  const [expanded, setExpanded] = useState(false);

  const handlePriceChange = (level, field, value) => {
    setPrices(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [field]: field === "price" ? parseFloat(value) || 0 : value
      }
    }));
  };

  const handleSave = async () => {
    await onSave(product.id, prices);
    setEditMode(false);
  };

  const category = CATEGORIES[product.category] || CATEGORIES.diagnostic;

  return (
    <Card className="overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-slate-400" />
            <div>
              <h3 className="font-medium">{product.label}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded ${category.color}`}>
                  {category.label}
                </span>
                <span className="text-xs text-slate-500">
                  {product.unit === "m2" ? "€/m²" : product.unit === "fenetre" ? "€/fenêtre" : "forfait"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-slate-500">Fourchette de prix</p>
              <p className="font-medium">
                {formatCurrency(prices.low?.price)} - {formatCurrency(prices.high?.price)}
              </p>
            </div>
            {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
      </div>

      {expanded && (
        <CardContent className="border-t bg-slate-50 pt-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-sm text-slate-700">Détail des prix par gamme</h4>
            {!editMode ? (
              <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>
                <Edit2 className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>
                  Annuler
                </Button>
                <Button size="sm" onClick={handleSave} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {["low", "medium", "high"].map((level) => (
              <div 
                key={level} 
                className={`p-4 rounded-lg border ${
                  level === "low" ? "bg-green-50 border-green-200" :
                  level === "medium" ? "bg-blue-50 border-blue-200" :
                  "bg-purple-50 border-purple-200"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    level === "low" ? "bg-green-200 text-green-800" :
                    level === "medium" ? "bg-blue-200 text-blue-800" :
                    "bg-purple-200 text-purple-800"
                  }`}>
                    {prices[level]?.label || level}
                  </span>
                </div>

                {editMode ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Prix</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={prices[level]?.price || ""}
                          onChange={(e) => handlePriceChange(level, "price", e.target.value)}
                          className="h-9"
                        />
                        <span className="text-sm text-slate-500">€</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Label</Label>
                      <Input
                        value={prices[level]?.label || ""}
                        onChange={(e) => handlePriceChange(level, "label", e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description</Label>
                      <Input
                        value={prices[level]?.description || ""}
                        onChange={(e) => handlePriceChange(level, "description", e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-2xl font-bold mb-1">
                      {formatCurrency(prices[level]?.price)}
                    </p>
                    <p className="text-xs text-slate-600">
                      {prices[level]?.description}
                    </p>
                    {prices[level]?.etas && (
                      <p className="text-xs text-slate-500 mt-1">
                        ETAS: {prices[level].etas}%
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {product.includes && (
            <div className="mt-4 text-xs text-slate-500">
              <strong>Inclus :</strong> {product.includes.join(", ")}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function AdminMarketPricesPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState({});
  const [metadata, setMetadata] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  // Charger les données
  const loadData = async () => {
    setLoading(true);
    try {
      const [pricesRes, historyRes] = await Promise.all([
        axios.get(`${API}/market-prices`),
        axios.get(`${API}/admin/market-prices/history`)
      ]);
      
      setProducts(pricesRes.data.products || {});
      setMetadata(pricesRes.data.metadata || {});
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement des prix");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Sauvegarder les prix d'un produit
  const handleSave = async (productId, newPrices) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/market-prices/${productId}`, {
        product_id: productId,
        prices: newPrices
      });
      
      // Mettre à jour localement
      setProducts(prev => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          prices: newPrices
        }
      }));
      
      toast.success("Prix mis à jour avec succès");
      
      // Recharger l'historique
      const historyRes = await axios.get(`${API}/admin/market-prices/history`);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error("Erreur sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Filtrer les produits par catégorie
  const filteredProducts = Object.entries(products).filter(([id, product]) => {
    if (activeCategory === "all") return true;
    return product.category === activeCategory;
  });

  // Stats
  const stats = {
    totalProducts: Object.keys(products).length,
    categories: [...new Set(Object.values(products).map(p => p.category))].length,
    lastUpdate: metadata.last_updated ? new Date(metadata.last_updated).toLocaleDateString('fr-FR') : "N/A"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A4D2E]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-xl font-bold text-[#1A4D2E]">Prix du marché</h1>
                <p className="text-sm text-slate-500">
                  Gérez les prix moyens des travaux de rénovation
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Package className="h-8 w-8 text-[#1A4D2E]" />
                <div>
                  <p className="text-2xl font-bold">{stats.totalProducts}</p>
                  <p className="text-sm text-slate-500">Produits</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.categories}</p>
                  <p className="text-sm text-slate-500">Catégories</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <History className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.lastUpdate}</p>
                  <p className="text-sm text-slate-500">Dernière MAJ</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="prices" className="space-y-6">
          <TabsList>
            <TabsTrigger value="prices">Prix par produit</TabsTrigger>
            <TabsTrigger value="history">Historique</TabsTrigger>
          </TabsList>

          <TabsContent value="prices">
            {/* Filtres par catégorie */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Button
                variant={activeCategory === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveCategory("all")}
              >
                Tous
              </Button>
              {Object.entries(CATEGORIES).map(([key, cat]) => (
                <Button
                  key={key}
                  variant={activeCategory === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveCategory(key)}
                >
                  {cat.label}
                </Button>
              ))}
            </div>

            {/* Liste des produits */}
            <div className="space-y-4">
              {filteredProducts.map(([id, product]) => (
                <ProductPriceEditor
                  key={id}
                  product={{ id, ...product }}
                  onSave={handleSave}
                  loading={saving}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Historique des modifications</CardTitle>
                <CardDescription>
                  Les 50 dernières modifications des prix
                </CardDescription>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Aucune modification enregistrée
                  </p>
                ) : (
                  <div className="space-y-2">
                    {history.slice().reverse().map((entry, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <div>
                            <p className="text-sm font-medium">
                              {entry.action === "update" ? "Mise à jour des prix" : entry.action}
                            </p>
                            <p className="text-xs text-slate-500">
                              Par {entry.updated_by || "admin"}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(entry.timestamp).toLocaleString('fr-FR')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Note d'information */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Information importante</p>
              <p>
                Les prix modifiés ici sont utilisés pour les estimations du mini-audit. 
                Ils doivent refléter les prix moyens du marché incluant la pose par un 
                professionnel RGE. Ces prix influencent le calcul du reste à charge 
                présenté aux bénéficiaires.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
